import type { FC } from 'react';
import Chart from './Chart';
import merge from 'lodash/merge';
import { defaultBasicAreaOptions } from './shared';
import { Consumer } from '@/context/HighChartsProvider';
import type { AnalyticsHandler } from '@/constants/tracking';

interface ChartData {
  name: string;
  data: number[][];
}

interface Props {
  analyticsHandler: AnalyticsHandler;
  currencyISO: string;
  currencySymbol?: string;
  chartData: ChartData[];
  portfolioId: string;
  lastUpdated: number;
}

const formatYAxis = (value, symbol) => {
  let valueSymbol = [value, symbol];

  if (Math.abs(value) > 1000000) {
    valueSymbol = [value / 1000000, 'M'];
  } else if (Math.abs(value) > 1000) {
    valueSymbol = [value / 1000, 'K'];
  }

  return valueSymbol;
};

const intervalCalc = (chartData: ChartData[]) => {
  const pfLength =
    chartData && chartData.length && chartData.length > 0
      ? chartData[0].data.length
      : 0;

  if (pfLength > 208) {
    //greater than 3 years
    return 24 * 3600 * 1000 * 365;
  } else if (pfLength > 52) {
    //greater than 1 year make monthly
    return 24 * 3600 * 1000 * 90;
  }

  return 24 * 3600 * 1000 * 30;
};

const HistoricalPortfolioValueChart: FC<Props> = ({
  currencyISO,
  lastUpdated,
  currencySymbol,
  chartData,
  analyticsHandler,
  portfolioId,
}) => (
  <Consumer>
    {(value) => {
      if (!value) return;
      const HighCharts = value.highCharts;

      const options = merge(
        {},
        defaultBasicAreaOptions(HighCharts, currencySymbol),
        {
          chart: {
            height: 350,
            marginLeft: 60,
            marginRight: 10,
          },
          legend: {
            enabled: true,
          },
          tooltip: {
            formatter: function () {
              const closure = this as any;
              const WHEN = new Date(closure.x);
              const text =
                WHEN.toDateString() +
                '<br>' +
                closure.series.name +
                ': <strong>' +
                (currencySymbol || '') +
                HighCharts.numberFormat(closure.y, 2) +
                '</strong>' +
                '<br>Total: <strong>' +
                (currencySymbol || '') +
                HighCharts.numberFormat(closure.total, 2) +
                '</strong>';
              return text;
            },
          },
          xAxis: {
            type: 'datetime',
            dateTimeLabelFormats: {
              month: '%b %y',
              year: '%Y',
            },
            tickInterval: intervalCalc(chartData),
          },
          yAxis: {
            title: {
              text: currencyISO,
              x: 40,
            },
            min: 0,
            reversedStacks: false,
            labels: {
              enabled: true,
              formatter: function () {
                const closure = this as any;
                if (closure.isFirst === true || closure.isLast === true) {
                  let retString = currencySymbol ? currencySymbol : '';

                  if (closure.value === 0) {
                    retString = retString + closure.value;
                  } else {
                    const valueSymbol = formatYAxis(closure.value, '');
                    retString = retString + valueSymbol[0] + valueSymbol[1];
                  }
                  return retString;
                }
              },
            },
          },
          plotOptions: {
            series: {
              stacking: 'normal',
              fillOpacity: 0.8,
              lineWidth: 0,
              marker: {
                enabled: false,
                states: {
                  hover: {
                    enabled: true,
                  },
                },
              },
              events: {
                legendItemClick: function (options) {
                  const legend =
                    (options && options.target && options.target.name) || '';
                  const status =
                    options && options.target && !options.target.visible;
                  if (analyticsHandler)
                    analyticsHandler(
                      'HistoricalPortfolioValueChart',
                      legend,
                      status
                    );
                },
              },
            },
          },
          series: chartData,
        }
      );

      return (
        <Chart options={options} modifier={`${portfolioId}-${lastUpdated}`} />
      );
    }}
  </Consumer>
);

export default HistoricalPortfolioValueChart;
